import React from 'react';
import { matchButtonLabel, normalizeReferenceUrl } from './siteSpec';

/**
 * Paste a site, get its design.
 *
 * This is a first-class control rather than something you have to know to ask
 * the chat for, because it is the shortest path to a site that looks like the
 * user's: they already have a main site, and the docs subdomain should not look
 * like a stranger.
 *
 * It sends an instruction to the SAME chat agent rather than calling a separate
 * endpoint — so what happened stays visible in the transcript, the agent can
 * report what it could not read, and the user can follow up in words
 * ("keep the colours but use the serif").
 */
export function MatchSiteBar({
  onMatch,
  disabled,
  suggestedUrl,
  savedUrl,
}: {
  onMatch: (url: string, refine: boolean) => void;
  disabled: boolean;
  suggestedUrl?: string | null;
  /** The site this design is already being matched to, if any. */
  savedUrl?: string | null;
}) {
  const [value, setValue] = React.useState(savedUrl || '');
  const [error, setError] = React.useState('');

  // Adopt the remembered site once it arrives, but never overwrite something
  // half-typed — the scaffold loads a moment after the pane does.
  const touched = React.useRef(false);
  React.useEffect(() => {
    if (!touched.current && savedUrl) setValue(savedUrl);
  }, [savedUrl]);

  const label = matchButtonLabel(savedUrl, value);

  const submit = () => {
    const url = normalizeReferenceUrl(value);
    if (!url) {
      setError('That doesn’t look like a site address - try acme.com');
      return;
    }
    setError('');
    onMatch(url, label === 'Refine the match');
  };

  return (
    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
      <div className="flex items-center gap-2 flex-wrap">
        <label
          htmlFor="match-site-url"
          className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
        >
          Match a site
        </label>
        <input
          id="match-site-url"
          type="text"
          value={value}
          onChange={(e) => {
            touched.current = true;
            setValue(e.target.value);
            if (error) setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={suggestedUrl || 'yoursite.com'}
          disabled={disabled}
          className="flex-1 min-w-[12rem] px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || !value.trim()}
          className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
        >
          {label}
        </button>
      </div>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        {error ? (
          <span className="text-red-600 dark:text-red-400">{error}</span>
        ) : (
          <>
            {label === 'Refine the match'
              ? 'Takes a picture of this site and that one, compares them, and closes the gap. The colors stay as measured.'
              : 'Opens the page in a browser and reads the colors, fonts, header and navigation it actually renders - then applies them here.'}
          </>
        )}
      </p>
    </div>
  );
}

export default MatchSiteBar;
